import { UploadTask } from "@/domain/upload/entities/UploadTask";
import { FileType } from "@/domain/upload/value-objects/FileType";
import { UploadStatus } from "@/domain/upload/value-objects/UploadStatus";
import { UploadCompletionDTO } from "../dtos/UploadCompletionDTO";
import { UploadFileRequestDTO } from "../dtos/UploadFileRequestDTO";
import { TextFileDraftDTO } from "../dtos/TextFileDraftDTO";
import {
  UploadPlaceholderRecordDTO,
  UploadTaskDTO,
} from "../dtos/UploadTaskDTO";
import {
  CreateMediaRecordRequest,
  FileUploadRepository,
} from "../ports/FileUploadRepository";
import { FileTypeDetector } from "../ports/FileTypeDetector";
import { MediaDurationService } from "../ports/MediaDurationService";
import {
  StorageUploadError,
  StorageUploadService,
} from "../ports/StorageUploadService";
import { TextFileReader } from "../ports/TextFileReader";
import { UploadFilePresenter } from "../ports/UploadFilePresenter";
import { UuidGenerator } from "../ports/UuidGenerator";

const DEFAULT_PLACEHOLDER_PROJECT = "";
const DEFAULT_PLACEHOLDER_STATUS = "Uploaded";

export class UploadFileUseCase {
  constructor(
    private readonly repository: FileUploadRepository,
    private readonly storageUploadService: StorageUploadService,
    private readonly textFileReader: TextFileReader,
    private readonly mediaDurationService: MediaDurationService,
    private readonly fileTypeDetector: FileTypeDetector,
    private readonly uuidGenerator: UuidGenerator
  ) {}

  async execute(
    request: UploadFileRequestDTO,
    presenter: UploadFilePresenter
  ): Promise<void> {
    const { file, projectId, uploadSource } = request;
    const fileId = this.uuidGenerator.generate();
    const fileType = this.fileTypeDetector.detect(file);
    const isSupported = this.fileTypeDetector.isSupported(file);
    const createdAt = new Date().toISOString();
    const baseName = this.removeFileExtension(file.name);
    const extension = this.extractExtension(file.name);

    const task = new UploadTask({
      id: fileId,
      name: baseName,
      originalFileName: file.name,
      type: fileType,
      createdAt,
      status: this.getInitialStatus(fileType),
      progress: 0,
    });

    presenter.presentTaskCreated(this.toDTO(task));

    if (!isSupported) {
      this.handleError(task, presenter, "Unsupported file type");
      return;
    }

    const sizeMb = this.calculateSizeMb(file.size);
    const hasSpace = await this.repository.checkSpace(sizeMb);
    if (!hasSpace) {
      this.handleError(
        task,
        presenter,
        "Not enough space.\nConsider deleting old records."
      );
      return;
    }

    // Check Firebase authentication before proceeding with upload
    // This helps catch authentication issues early for specific users
    if (fileType !== "text") {
      try {
        const { firebaseAuth } = await import("@/lib/firebase");
        if (!firebaseAuth?.currentUser?.uid) {
          this.handleError(
            task,
            presenter,
            "Please refresh the page and try again. Authentication session may have expired."
          );
          return;
        }
      } catch (authError) {
        console.error("[UploadFileUseCase] Firebase auth check failed:", authError);
        this.handleError(
          task,
          presenter,
          "Authentication error. Please refresh the page and try again."
        );
        return;
      }
    }

    if (fileType === "text") {
      await this.handleTextFile({
        task,
        presenter,
        file,
        projectId,
        extension: extension as TextFileDraftDTO["extension"],
        uploadSource,
      });
      return;
    }

    await this.handleMediaFile({
      task,
      presenter,
      file,
      projectId,
      sizeMb,
      extension,
      fileType,
    });
  }

  private getInitialStatus(fileType: FileType): UploadStatus {
    return fileType === "text" ? "pending_modal" : "pending";
  }

  private async handleTextFile({
    task,
    presenter,
    file,
    projectId,
    extension,
    uploadSource,
  }: {
    task: UploadTask;
    presenter: UploadFilePresenter;
    file: File;
    projectId?: string;
    extension: TextFileDraftDTO["extension"];
    uploadSource?: string;
  }): Promise<void> {
    try {
      task.markPendingModal();
      presenter.presentTaskUpdated(this.toDTO(task));

      const content = await this.textFileReader.readContent(file);
      const draft: TextFileDraftDTO = {
        taskId: task.id,
        name: task.name,
        content,
        extension,
        projectId,
        uploadSource,
      };

      presenter.presentTextFileDraft(draft);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to process text file";
      this.handleError(task, presenter, message);
    }
  }

  private async handleMediaFile({
    task,
    presenter,
    file,
    projectId,
    sizeMb,
    extension,
    fileType,
  }: {
    task: UploadTask;
    presenter: UploadFilePresenter;
    file: File;
    projectId?: string;
    sizeMb: number;
    extension: string;
    fileType: FileType;
  }): Promise<void> {
    try {
      task.markUploading();
      presenter.presentTaskUpdated(this.toDTO(task));

      const durationSec = await this.getDurationOrFallback(file);

      const mediaType: "audio" | "video" =
        fileType === "video" ? "video" : "audio";

      const metadata = this.buildMetadata({
        taskId: task.id,
        durationSec,
        name: task.name,
        sizeMb,
        type: mediaType,
        extension,
        contentType: file.type || undefined,
      });

      const controller = this.storageUploadService.startUpload({
        taskId: task.id,
        file,
        fileName: file.name,
        fileType,
        metadata,
        onProgress: (progress) =>
          presenter.presentUploadProgress(task.id, progress),
        onError: (uploadError) =>
          this.handleStorageError(task, presenter, uploadError),
        onSuccess: async (result) => {
          await this.handleUploadSuccess({
            task,
            presenter,
            projectId,
            storageFullPath: result.fullPath,
            sizeMb,
            durationSec,
            fileType: mediaType,
            extension,
            fileName: file.name,
          });
        },
      });

      presenter.presentUploadController(task.id, controller);
    } catch (error) {
      // Log error for debugging user-specific issues
      console.error("[UploadFileUseCase] Error in handleMediaFile:", {
        taskId: task.id,
        fileName: file.name,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      const message =
        error instanceof Error ? error.message : "Failed to process media file";
      this.handleError(task, presenter, message);
    }
  }

  private async handleUploadSuccess({
    task,
    presenter,
    projectId,
    storageFullPath,
    sizeMb,
    durationSec,
    fileType,
    extension,
    fileName,
  }: {
    task: UploadTask;
    presenter: UploadFilePresenter;
    projectId?: string;
    storageFullPath: string;
    sizeMb: number;
    durationSec: number;
    fileType: "audio" | "video";
    extension: string;
    fileName: string;
  }): Promise<void> {
    try {
      const createRequest: CreateMediaRecordRequest = {
        recordName: task.name,
        filePath: storageFullPath,
        durationSec,
        sizeMb,
        type: fileType,
      };

      const uploadResult = await this.repository.createMediaRecord(
        createRequest,
        projectId
      );

      task.markCompleted({
        sizeMb,
        durationSec,
        placeholderRecordId: uploadResult.id,
      });

      const completionPayload: UploadCompletionDTO = {
        taskId: task.id,
        placeholderRecord: this.createPlaceholderRecord({
          recordId: uploadResult.id,
          recordName: task.name,
          fileExtension: extension,
          fileName,
          sizeMb,
          durationSec,
          timestamp: new Date().toISOString(),
          type: fileType,
        }),
        sizeMb,
        durationSec,
      };

      presenter.presentTaskUpdated(this.toDTO(task));
      presenter.presentUploadCompleted(completionPayload);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create record after upload";
      this.handleError(task, presenter, message);
    }
  }

  private handleStorageError(
    task: UploadTask,
    presenter: UploadFilePresenter,
    error: StorageUploadError
  ): void {
    const message = this.mapStorageError(error);
    this.handleError(task, presenter, message);
  }

  private handleError(
    task: UploadTask,
    presenter: UploadFilePresenter,
    message: string
  ): void {
    task.markError(message);
    presenter.presentTaskUpdated(this.toDTO(task));
    presenter.presentUploadError(task.id, message);
  }

  private buildMetadata({
    taskId,
    durationSec,
    name,
    sizeMb,
    type,
    extension,
    contentType,
  }: {
    taskId: string;
    durationSec: number;
    name: string;
    sizeMb: number;
    type: "audio" | "video";
    extension: string;
    contentType: string | undefined;
  }) {
    return {
      contentType,
      customMetadata: {
        duration: durationSec.toString(),
        name,
        fileId: taskId,
        size: sizeMb.toString(),
        type,
        extension,
      },
    };
  }

  private toDTO(task: UploadTask): UploadTaskDTO {
    const snapshot = task.toSnapshot();
    return {
      id: snapshot.id,
      name: snapshot.name,
      originalFileName: snapshot.originalFileName,
      type: snapshot.type,
      status: snapshot.status,
      progress: snapshot.progress,
      createdAt: snapshot.createdAt,
      sizeMb: snapshot.sizeMb,
      durationSec: snapshot.durationSec ?? null,
      error: snapshot.error,
    };
  }

  private createPlaceholderRecord({
    recordId,
    recordName,
    fileExtension,
    fileName,
    sizeMb,
    durationSec,
    timestamp,
    type,
  }: {
    recordId: string;
    recordName: string;
    fileExtension: string;
    fileName: string;
    sizeMb: number;
    durationSec: number;
    timestamp: string;
    type: FileType;
  }): UploadPlaceholderRecordDTO {
    return {
      id: recordId,
      name: recordName,
      project: DEFAULT_PLACEHOLDER_PROJECT,
      status: DEFAULT_PLACEHOLDER_STATUS,
      fileName,
      fileExtension,
      sizeMb,
      durationSec: Number.isFinite(durationSec) ? durationSec : null,
      createdAt: timestamp,
      updatedAt: timestamp,
      type,
    };
  }

  private async getDurationOrFallback(file: File): Promise<number> {
    try {
      return await this.mediaDurationService.getDuration(file);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      // As a fallback we approximate using file size when duration cannot be read
      return this.calculateApproximateDuration(file);
    }
  }

  private calculateApproximateDuration(file: File): number {
    const extension = this.extractExtension(file.name).toLowerCase();
    if (extension === "amr") {
      const fileSizeInBits = file.size * 8;
      const bitRate = 12200; // 12.2 kbit/s for AMR-NB
      return fileSizeInBits / bitRate;
    }
    const averageBitrate = 128000; // 128 kbps default fallback
    return (file.size * 8) / averageBitrate;
  }

  private mapStorageError(error: StorageUploadError): string {
    if (error.code === "storage/unauthorized") {
      return "Authorization error or wrong file type";
    }

    if (error.code === "storage/canceled") {
      return "Upload canceled";
    }

    return error.message || "Error uploading file";
  }

  private removeFileExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, "");
  }

  private extractExtension(fileName: string): string {
    return fileName.split(".").pop()?.toLowerCase() ?? "";
  }

  private calculateSizeMb(sizeInBytes: number): number {
    return sizeInBytes / (1024 * 1024);
  }
}
